import React, { useState } from 'react';
import { askQuestion } from '../services/api';

function QuestionPanel() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const sampleQuestions = [
    "How much did I spend this week?",
    "What are my top spending categories?",
    "Show me all grocery expenses",
    "How much did I spend at Starbucks?",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await askQuestion(query);
      setResult(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSampleClick = (question) => {
    setQuery(question);
  };

  const styles = {
    container: {
      padding: 'var(--space-6)',
      maxWidth: '900px',
      margin: '0 auto',
    },
    form: {
      marginBottom: 'var(--space-8)',
    },
    inputGroup: {
      position: 'relative',
      marginBottom: 'var(--space-4)',
    },
    textarea: {
      width: '100%',
      padding: 'var(--space-3)',
      borderRadius: 'var(--radius-default)',
      border: '2px solid var(--color-border-default)',
      fontSize: 'var(--font-size-base)',
      fontFamily: 'inherit',
      resize: 'vertical',
      minHeight: '120px',
      outline: 'none',
      transition: 'var(--transition-normal)',
      backgroundColor: 'var(--color-bg-base)',
      color: 'var(--color-text-primary)',
    },
    button: {
      padding: 'var(--space-3) var(--space-6)',
      borderRadius: 'var(--radius-default)',
      border: 'none',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
      backgroundColor: 'var(--color-primary)',
      color: 'var(--color-text-inverse)',
      width: '100%',
    },
    disabledButton: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    sampleQuestions: {
      marginBottom: 'var(--space-8)',
    },
    sampleButton: {
      padding: 'var(--space-2) var(--space-4)',
      borderRadius: '9999px',
      border: '1px solid var(--color-border-default)',
      backgroundColor: 'var(--color-bg-base)',
      fontSize: 'var(--font-size-sm)',
      cursor: 'pointer',
      marginRight: 'var(--space-2)',
      marginBottom: 'var(--space-2)',
      display: 'inline-block',
      transition: 'var(--transition-normal)',
      color: 'var(--color-text-secondary)',
      fontWeight: 'var(--font-weight-medium)',
    },
    error: {
      padding: 'var(--space-3)',
      borderRadius: 'var(--radius-default)',
      backgroundColor: 'var(--color-error-light)',
      color: 'var(--color-error-dark)',
      marginBottom: 'var(--space-4)',
      fontSize: 'var(--font-size-sm)',
    },
    result: {
      marginTop: 'var(--space-8)',
      padding: 'var(--space-6)',
      borderRadius: 'var(--radius-lg)',
      backgroundColor: 'var(--color-bg-subtle)',
      border: '1px solid var(--color-border-light)',
      boxShadow: 'var(--shadow-sm)',
    },
    answer: {
      fontSize: 'var(--font-size-base)',
      lineHeight: 'var(--line-height-relaxed)',
      color: 'var(--color-text-primary)',
      marginBottom: 'var(--space-6)',
      whiteSpace: 'pre-wrap',
    },
    loader: {
      display: 'inline-block',
      width: '16px',
      height: '16px',
      border: '2px solid var(--color-border-light)',
      borderTop: '2px solid var(--color-primary)',
      borderRadius: 'var(--radius-full)',
      animation: 'spin 1s linear infinite',
      marginRight: 'var(--space-2)',
    },
  };

  return (
    <div style={styles.container}>
      <h2 style={{
        fontSize: 'var(--font-size-2xl)',
        fontWeight: 'var(--font-weight-semibold)',
        letterSpacing: '-0.025em',
        marginBottom: 'var(--space-6)',
        color: 'var(--color-text-primary)',
        margin: `0 0 var(--space-6) 0`
      }}>
        Ask AI Assistant 🤖
      </h2>

      <div style={styles.sampleQuestions}>
        <p style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          marginBottom: 'var(--space-3)',
          margin: `0 0 var(--space-3) 0`
        }}>
          💡 Try these questions:
        </p>
        {sampleQuestions.map((question, idx) => (
          <button
            key={idx}
            onClick={() => handleSampleClick(question)}
            style={styles.sampleButton}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color-primary-subtle)';
              e.target.style.borderColor = 'var(--color-primary)';
              e.target.style.color = 'var(--color-primary)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color-bg-base)';
              e.target.style.borderColor = 'var(--color-border-default)';
              e.target.style.color = 'var(--color-text-secondary)';
            }}
          >
            {question}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.inputGroup}>
          <textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask me anything about your expenses..."
            style={styles.textarea}
            disabled={loading}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--color-primary)';
              e.target.style.boxShadow = '0 0 0 2px white, 0 0 0 4px #3B82F6';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--color-border-default)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <button
          type="submit"
          disabled={loading || !query.trim()}
          style={{
            ...styles.button,
            ...(loading || !query.trim() ? styles.disabledButton : {}),
          }}
          onMouseEnter={(e) => {
            if (!loading && query.trim()) {
              e.target.style.backgroundColor = 'var(--color-primary-hover)';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading && query.trim()) {
              e.target.style.backgroundColor = 'var(--color-primary)';
            }
          }}
        >
          {loading && <div style={styles.loader}></div>}
          {loading ? 'Thinking...' : '✨ Ask Question'}
        </button>
      </form>

      {error && (
        <div style={styles.error}>
          <strong>⚠️ Error: </strong>{error}
        </div>
      )}

      {result && (
        <div style={styles.result}>
          <h3 style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semibold)',
            letterSpacing: '-0.025em',
            marginBottom: 'var(--space-4)',
            color: 'var(--color-text-primary)',
            margin: `0 0 var(--space-4) 0`
          }}>
            💬 Answer
          </h3>
          <div style={styles.answer}>{result.answer}</div>

        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default QuestionPanel;
